import { useContext } from 'react';
import type { Context } from 'react';

/**
 * Like `useContext`, but asserts a provider is mounted above. A missing
 * provider is detected by object identity against `unprovidedValue` — the
 * module-private value the context was created with — so the check fires even
 * when that default is a fully-populated object rather than `undefined`.
 *
 * Use for consumers that cannot function without a provider, where inheriting
 * the context's default would be a silent bug rather than a safe fallback.
 */
export function useProvidedContext<T>(
  context: Context<T>,
  unprovidedValue: T,
  providerName: string,
): T {
  const value = useContext(context);
  if (value === unprovidedValue) {
    throw new Error(
      `DEVELOPER ERROR: no <${providerName}> found above this component; wrap the tree in one.`,
    );
  }
  return value;
}

/**
 * Non-throwing sibling of `useProvidedContext`: returns `undefined` when no
 * provider is mounted above, letting a consumer branch on presence instead of
 * inheriting the context default.
 */
export function useOptionalContext<T>(context: Context<T>, unprovidedValue: T): T | undefined {
  const value = useContext(context);
  return value === unprovidedValue ? undefined : value;
}
